import React from 'react';
import { Card, Progress, Typography, Alert, Spin, Row, Col, Statistic, Space } from 'antd';
import { ThunderboltOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useGetAIQuotaStatusQuery } from '../services/nestjs-base-api';

const { Text, Title } = Typography;

interface AIQuotaWidgetProps {
    compact?: boolean; // Compact mode shows less detail
    showTitle?: boolean;
}

export const AIQuotaWidget: React.FC<AIQuotaWidgetProps> = ({ compact = false, showTitle = true }) => {
    const { user } = useAuth();
    const { data: quotaStatus, isLoading, error } = useGetAIQuotaStatusQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !user?.jwtToken, pollingInterval: 60000 } // Refresh every minute
    );

    if (!user?.jwtToken) {
        return null;
    }

    if (isLoading) {
        return (
            <Card size="small">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                </div>
            </Card>
        );
    }

    if (error || !quotaStatus) {
        return null; // Silently fail if quota service is unavailable
    }

    const getStatusColor = (percentageUsed: number, enforcementEnabled: boolean) => {
        // If enforcement is disabled, always show blue (informational)
        if (!enforcementEnabled) return 'blue';

        if (percentageUsed >= 100) return 'red';
        if (percentageUsed >= 90) return 'orange';
        if (percentageUsed >= 80) return 'gold';
        return 'green';
    };

    const getStatusIcon = () => {
        // If enforcement is disabled, show info icon
        if (!quotaStatus.enforcementEnabled) {
            return <CheckCircleOutlined style={{ color: 'blue' }} />;
        }

        if (!quotaStatus.withinQuota) {
            return <WarningOutlined style={{ color: 'red' }} />;
        }
        if (quotaStatus.warningTriggered) {
            return <WarningOutlined style={{ color: 'orange' }} />;
        }
        if (quotaStatus.alertTriggered) {
            return <WarningOutlined style={{ color: 'gold' }} />;
        }
        return <CheckCircleOutlined style={{ color: 'green' }} />;
    };

    const formatTokens = (tokens: number) => {
        if (tokens >= 1000000) {
            return `${(tokens / 1000000).toFixed(1)}M`;
        }
        if (tokens >= 1000) {
            return `${(tokens / 1000).toFixed(1)}K`;
        }
        return tokens.toLocaleString();
    };

    if (compact) {
        const primaryQuota = quotaStatus.limitType === 'daily' ? quotaStatus.daily : quotaStatus.monthly;
        const primaryLabel = quotaStatus.limitType === 'daily' ? 'Daily' : 'Monthly';

        return (
            <Card size="small" style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            <ThunderboltOutlined />
                            <Text strong>{primaryLabel} AI Usage</Text>
                        </Space>
                        {getStatusIcon()}
                    </div>
                    <Progress
                        percent={quotaStatus.enforcementEnabled ? Math.round(primaryQuota.percentageUsed) : 0}
                        strokeColor={getStatusColor(primaryQuota.percentageUsed, quotaStatus.enforcementEnabled)}
                        size="small"
                        showInfo={false}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {formatTokens(primaryQuota.usage)} {quotaStatus.enforcementEnabled ? `/ ${formatTokens(primaryQuota.limit)}` : ''} tokens
                        {!quotaStatus.enforcementEnabled && ' (no limit)'}
                    </Text>
                </Space>
            </Card>
        );
    }

    return (
        <Card>
            {showTitle && (
                <Title level={5} style={{ marginBottom: 16 }}>
                    <ThunderboltOutlined style={{ marginRight: 8 }} />
                    AI Token Usage
                </Title>
            )}

            {!quotaStatus.enforcementEnabled && (
                <Alert
                    message="Quota Enforcement Disabled"
                    description="AI usage tracking is active but quotas are not enforced. You can monitor token consumption without limits."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {quotaStatus.enforcementEnabled && !quotaStatus.withinQuota && (
                <Alert
                    message="AI Quota Exceeded"
                    description={`Your organization has reached its ${quotaStatus.limitType} AI token limit. AI features are temporarily unavailable.`}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {quotaStatus.enforcementEnabled && quotaStatus.withinQuota && quotaStatus.warningTriggered && (
                <Alert
                    message="AI Quota Warning"
                    description="Your organization is approaching its AI token limit. Please contact your administrator to upgrade your plan."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {quotaStatus.enforcementEnabled && quotaStatus.withinQuota && quotaStatus.alertTriggered && !quotaStatus.warningTriggered && (
                <Alert
                    message="AI Usage Alert"
                    description="Your organization has used a significant portion of its AI quota."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={16}>
                <Col span={12}>
                    <Card size="small">
                        <Statistic
                            title="Monthly Usage"
                            value={quotaStatus.enforcementEnabled ? quotaStatus.monthly.percentageUsed : quotaStatus.monthly.usage}
                            precision={quotaStatus.enforcementEnabled ? 1 : 0}
                            suffix={quotaStatus.enforcementEnabled ? '%' : ' tokens'}
                            valueStyle={{ color: getStatusColor(quotaStatus.monthly.percentageUsed, quotaStatus.enforcementEnabled) }}
                        />
                        <Progress
                            percent={quotaStatus.enforcementEnabled ? Math.round(quotaStatus.monthly.percentageUsed) : 0}
                            strokeColor={getStatusColor(quotaStatus.monthly.percentageUsed, quotaStatus.enforcementEnabled)}
                            size="small"
                            showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatTokens(quotaStatus.monthly.usage)} {quotaStatus.enforcementEnabled ? `/ ${formatTokens(quotaStatus.monthly.limit)}` : '(no limit)'} tokens
                        </Text>
                    </Card>
                </Col>

                <Col span={12}>
                    <Card size="small">
                        <Statistic
                            title="Daily Usage"
                            value={quotaStatus.enforcementEnabled ? quotaStatus.daily.percentageUsed : quotaStatus.daily.usage}
                            precision={quotaStatus.enforcementEnabled ? 1 : 0}
                            suffix={quotaStatus.enforcementEnabled ? '%' : ' tokens'}
                            valueStyle={{ color: getStatusColor(quotaStatus.daily.percentageUsed, quotaStatus.enforcementEnabled) }}
                        />
                        <Progress
                            percent={quotaStatus.enforcementEnabled ? Math.round(quotaStatus.daily.percentageUsed) : 0}
                            strokeColor={getStatusColor(quotaStatus.daily.percentageUsed, quotaStatus.enforcementEnabled)}
                            size="small"
                            showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatTokens(quotaStatus.daily.usage)} {quotaStatus.enforcementEnabled ? `/ ${formatTokens(quotaStatus.daily.limit)}` : '(no limit)'} tokens
                        </Text>
                    </Card>
                </Col>
            </Row>

            {quotaStatus.enforcementEnabled && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {quotaStatus.monthly.remaining.toLocaleString()} tokens remaining this month
                    </Text>
                </div>
            )}
        </Card>
    );
};
